import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
} from "@angular/core";
import { DatePipe } from "@angular/common";
import { TranslateModule } from "@ngx-translate/core";
import { TablerIconsModule } from "angular-tabler-icons";
import { Router } from "@angular/router";
import { MaterialModule } from "src/app/material.module";
import { MatMenuModule } from "@angular/material/menu";
import {
  DEFAULT_POPOVER_POSITIONS,
  PopoverComponent,
} from "src/app/shared/components/treeslo-grid/popover";
import { ColCountDirective } from "src/app/shared/directives/col-count.directive";
import { ColSpanDirective } from "src/app/shared/directives/col-span.directive";
import { StoreFacadeService } from "../stores-facade.service";
import { StoreAutoRemovePopoverComponent } from "../store-auto-remove-popover/store-auto-remove-popover.component";
import { StoreGetDTO, PendingStoreInvitation } from "../store";
import {
  KNOWLEDGE_BASE_ARTICLE_STATE_KEY,
  KNOWLEDGE_BASE_RETURN_STATE_KEY,
} from "src/app/pages/help-center/knowledge-base/knowledge-base.model";

export type StoreCardMode = "owner" | "shared" | "pending";

@Component({
  selector: "app-store-card",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MaterialModule,
    MatMenuModule,
    TranslateModule,
    TablerIconsModule,
    DatePipe,
    PopoverComponent,
    ColCountDirective,
    ColSpanDirective,
  ],
  templateUrl: "./store-card.component.html",
  styleUrl: "./store-card.component.scss",
})
export class StoreCardComponent {
  readonly StoreAutoRemovePopoverComponent = StoreAutoRemovePopoverComponent;
  readonly autoRemovePopoverPositions = DEFAULT_POPOVER_POSITIONS;

  private readonly facade = inject(StoreFacadeService);
  private readonly router = inject(Router);

  private static readonly AUTO_REMOVE_KB_ARTICLE_ID =
    "store-management--understanding-auto-remove";
  private static readonly STORES_KB_RETURN_CONTEXT = {
    url: "/store/stores",
    labelKey: "knowledge_base.return.stores",
  } as const;

  // ---- Inputs ----
  readonly mode = input.required<StoreCardMode>();
  readonly store = input<StoreGetDTO | null>(null);
  readonly invitation = input<PendingStoreInvitation | null>(null);
  readonly enableSharedStores = input(false);
  readonly enableDeliveryAddressStep = input(false);
  readonly isAccepting = input(false);

  // ---- Outputs (owner / shared) ----
  readonly viewStore = output<StoreGetDTO>();
  readonly editStore = output<StoreGetDTO>();
  readonly shareStore = output<StoreGetDTO>();
  readonly deleteStore = output<StoreGetDTO>();
  readonly completeSetup = output<StoreGetDTO>();
  readonly updateDelivery = output<StoreGetDTO>();
  readonly updateThreshold = output<StoreGetDTO>();
  readonly revokeShare = output<{ store: StoreGetDTO; email: string }>();

  // ---- Output (pending) ----
  readonly acceptInvitation = output<PendingStoreInvitation>();

  // ---- Computed ----
  readonly currentProvider = computed(() =>
    this.mode() === "pending"
      ? this.invitation()?.provider
      : this.store()?.provider,
  );

  readonly currentName = computed(() =>
    this.mode() === "pending"
      ? this.invitation()?.name
      : this.store()?.name,
  );

  // ---- Template helpers ----
  isAmazonProvider(provider?: string): boolean {
    return !!provider && provider.toLowerCase().includes("amazon");
  }

  getProviderLogo(provider?: string): string {
    return this.facade.getProviderLogo(provider);
  }

  buildAutoRemovePopoverInputs(
    popover: PopoverComponent,
  ): Record<string, unknown> {
    return {
      closePopover: () => popover.close(),
      onLearnMore: () => this.navigateToAutoRemoveKnowledgeBase(popover),
    };
  }

  // ---- Action handlers ----
  onView(event: Event): void {
    event.stopPropagation();
    const s = this.store();
    if (s) this.viewStore.emit(s);
  }

  onEdit(event: Event): void {
    event.stopPropagation();
    const s = this.store();
    if (s) this.editStore.emit(s);
  }

  onShare(event: Event): void {
    event.stopPropagation();
    const s = this.store();
    if (s) this.shareStore.emit(s);
  }

  onDelete(event: Event): void {
    event.stopPropagation();
    const s = this.store();
    if (s) this.deleteStore.emit(s);
  }

  onCompleteSetup(event: Event): void {
    event.stopPropagation();
    const s = this.store();
    if (s) this.completeSetup.emit(s);
  }

  onUpdateDelivery(event: Event): void {
    event.stopPropagation();
    const s = this.store();
    if (s) this.updateDelivery.emit(s);
  }

  onUpdateThreshold(event: Event): void {
    event.stopPropagation();
    const s = this.store();
    if (s) this.updateThreshold.emit(s);
  }

  onAccept(event: Event): void {
    event.stopPropagation();
    const inv = this.invitation();
    if (inv) this.acceptInvitation.emit(inv);
  }

  onRevokeShare(event: Event, email: string): void {
    event.stopPropagation();
    const s = this.store();
    if (s) this.revokeShare.emit({ store: s, email });
  }

  private navigateToAutoRemoveKnowledgeBase(popover?: PopoverComponent): void {
    popover?.close();
    const returnContext = StoreCardComponent.STORES_KB_RETURN_CONTEXT;
    void this.router.navigate(["/help/knowledge-base"], {
      fragment: StoreCardComponent.AUTO_REMOVE_KB_ARTICLE_ID,
      queryParams: {
        returnUrl: returnContext.url,
        returnLabelKey: returnContext.labelKey,
      },
      state: {
        [KNOWLEDGE_BASE_RETURN_STATE_KEY]: returnContext,
        [KNOWLEDGE_BASE_ARTICLE_STATE_KEY]:
          StoreCardComponent.AUTO_REMOVE_KB_ARTICLE_ID,
      },
    });
  }
}
